import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, ScrollArea, Group, Text, useMantineTheme, ActionIcon, Modal, SimpleGrid, Card, Badge, Stack, Divider, useComputedColorScheme } from '@mantine/core';
import { IconList, IconToolsKitchen2, IconGlassFull } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { MenuItem, Menu } from '../../types/menu';
import { getCategoryList } from '../../utils/translationHelpers';
import { t } from '../../utils/uiTranslations';

interface CategoryNavProps {
  categories: string[];
  activeCategory: string | null;
  onCategoryChange: (category: string) => void;
  isScrollSpyActive?: boolean;
  onCategoryClick?: (category: string) => void;
  menuItems?: MenuItem[];
  currentLanguage?: string;
  menu?: Menu;
  onQuestionClick?: (question: string) => void;
  drinkCategories?: string[];
  allCategories?: string[];
  activeSection?: 'food' | 'drinks';
  onSectionChange?: (section: 'food' | 'drinks') => void;
}

// Custom hook for scroll spy functionality
const useScrollSpy = (categories: string[], isActive: boolean, onCategoryChange: (category: string) => void) => {
  const [customActiveCategory, setCustomActiveCategory] = useState<string | null>(null);
  const viewportHeightRef = useRef<number>(window.innerHeight);
  const isViewportChangingRef = useRef<boolean>(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const detectActiveCategory = useCallback(() => {
    // Skip detection if scroll spy is disabled or viewport is changing
    if (!isActive || isViewportChangingRef.current) return;

    const categoryElements = document.querySelectorAll('[data-category]');
    // Use a fixed threshold based on initial viewport height to prevent snapping issues
    // when keyboard opens/closes
    const threshold = viewportHeightRef.current * 0.3; // 30% from top of viewport
    
    let activeCategory: string | null = null;
    
    categoryElements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      
      if (rect.top <= threshold && rect.bottom > threshold) {
        activeCategory = element.getAttribute('data-category');
      }
    });
    
    if (activeCategory && activeCategory !== customActiveCategory) {
      console.log('CategoryNav - Scroll spy detected new active category:', {
        activeCategory,
        currentActiveCategory: customActiveCategory,
        allCategories: Array.from(categoryElements).map(el => el.getAttribute('data-category'))
      });
      setCustomActiveCategory(activeCategory);
      onCategoryChange(activeCategory);
    }
  }, [isActive, customActiveCategory, onCategoryChange]);

  // Handle viewport resize (keyboard open/close)
  useEffect(() => {
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDiff = Math.abs(currentHeight - viewportHeightRef.current);
      
      // If viewport height changed significantly (likely keyboard), temporarily disable scroll spy
      if (heightDiff > 50) {
        isViewportChangingRef.current = true;
        
        // Clear any existing timeout
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        
        // Re-enable scroll spy after viewport stabilizes
        resizeTimeoutRef.current = setTimeout(() => {
          isViewportChangingRef.current = false;
          // Update viewport height reference only after stabilization
          viewportHeightRef.current = window.innerHeight;
          // Re-detect active category with new viewport
          detectActiveCategory();
        }, 300);
      }
    };

    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [detectActiveCategory]);

  // Use a ref to store the scrollable container to avoid re-querying on every render
  const scrollableContainerRef = useRef<HTMLElement | null>(null);
  // Use a ref to store the detectActiveCategory callback to prevent re-initialization
  const detectActiveCategoryRef = useRef(detectActiveCategory);
  
  // Update the ref whenever detectActiveCategory changes, but don't re-initialize listener
  useEffect(() => {
    detectActiveCategoryRef.current = detectActiveCategory;
  }, [detectActiveCategory]);
  
  useEffect(() => {
    // Find the scrollable container (Box inside MenuLayout) only once
    // This is the actual element that scrolls, not the window
    if (!scrollableContainerRef.current) {
      const menuLayoutMain = document.getElementById('menu-layout-main');
      scrollableContainerRef.current = menuLayoutMain?.firstElementChild as HTMLElement | null;
    }
    
    const scrollableContainer = scrollableContainerRef.current;
    
    if (scrollableContainer) {
      // Create a stable listener that uses the ref to avoid re-initialization
      const listener = () => {
        // Only detect if scroll spy is active
        if (isActive) {
          detectActiveCategoryRef.current();
        }
      };
      
      // Listen to the container's scroll events, not window scroll
      scrollableContainer.addEventListener('scroll', listener, { passive: true });
      if (isActive) {
        detectActiveCategoryRef.current(); // Check initial state
      }
      
      return () => scrollableContainer.removeEventListener('scroll', listener);
    } else {
      // Fallback to window scroll if container not found (shouldn't happen in normal usage)
      const listener = () => {
        if (isActive) {
          detectActiveCategoryRef.current();
        }
      };
      
      window.addEventListener('scroll', listener, { passive: true });
      if (isActive) {
        detectActiveCategoryRef.current(); // Check initial state
      }
      
      return () => window.removeEventListener('scroll', listener);
    }
  }, [isActive]); // Only depend on isActive, not detectActiveCategory - this prevents re-initialization

  return customActiveCategory;
};

// Custom hook for horizontal scrolling
const useHorizontalScroll = (scrollAreaRef: React.RefObject<HTMLDivElement | null>) => {
  const scrollToCategory = useCallback((category: string, categoryRefs: React.MutableRefObject<Map<string, HTMLElement>>) => {
    if (!categoryRefs.current.has(category)) return;

    const element = categoryRefs.current.get(category);
    if (!element || !scrollAreaRef.current) return;

    // Removed console.log to reduce noise - only log in development if needed
    // console.log('CategoryNav - Scrolling to category:', {
    //   category,
    //   elementExists: !!element,
    //   containerExists: !!scrollAreaRef.current
    // });

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }, 100);
  }, [scrollAreaRef]);

  return scrollToCategory;
};

// Category item component
const CategoryItem: React.FC<{
  category: string;
  isActive: boolean;
  onClick: () => void;
  theme: any;
  currentLanguage: string;
}> = ({ category, isActive, onClick, theme, currentLanguage }) => {
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  return (
    <Text
      size="sm"
      fw={isActive ? 700 : 400}
      style={{
        cursor: 'pointer',
        padding: '8px 16px',
        borderRadius: 0,
        backgroundColor: 'transparent',
        color: isActive
          ? (isDarkMode ? theme.colors.orange[4] : theme.colors.orange[6])
          : (isDarkMode ? theme.colors.dark[0] : theme.colors.gray[6]),
        borderBottom: isActive
          ? `2px solid ${isDarkMode ? theme.colors.orange[4] : theme.colors.orange[6]}`
          : '2px solid transparent',
        whiteSpace: 'nowrap',
        transition: 'color 0.2s ease, border-color 0.2s ease',
      }}
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      tabIndex={0}
      aria-label={t('showCategoryAria', currentLanguage, { category })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {category}
    </Text>
  );
};

export const CategoryNav: React.FC<CategoryNavProps> = React.memo(({
  categories,
  activeCategory,
  onCategoryChange,
  isScrollSpyActive = true,
  onCategoryClick,
  menuItems = [],
  currentLanguage = 'en',
  menu,
  onQuestionClick,
  drinkCategories,
  allCategories,
  activeSection,
  onSectionChange,
}) => {
  const theme = useMantineTheme();
  const categoryTopImageAspectRatio = theme.other?.layout?.categoryTopImageAspectRatio ?? '16 / 9';
  const categoryTopImageMaxHeight = theme.other?.layout?.categoryTopImageMaxHeight ?? 220;
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Map<string, HTMLElement>>(new Map());
  
  // Sync modal state with URL parameter
  // When ?categories=true is in URL, modal should be open
  const modalOpened = searchParams.get('categories') === 'true';

  // Custom hooks
  // Scroll spy is now handled by unified scroll manager in Menu.tsx
  // We just use the activeCategory prop passed from Menu
  const scrollToCategory = useHorizontalScroll(scrollAreaRef);

  // Calculate item counts per category
  const categoryCounts = useMemo(() => {
    return menuItems.reduce((acc, item) => {
      const categories = getCategoryList(item, currentLanguage);
      const categoriesForCount = categories.length > 0 ? categories : ['Uncategorized'];
      categoriesForCount.forEach((category) => {
        acc[category] = (acc[category] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);
  }, [menuItems, currentLanguage]);

  // Handle category click
  const handleCategoryClick = useCallback((category: string) => {
    if (onCategoryClick) {
      onCategoryClick(category);
    }
  }, [onCategoryClick, categories]);

  // Handle opening the category modal (updates URL)
  const handleOpenModal = useCallback(() => {
    setSearchParams(prev => {
      prev.set('categories', 'true');
      return prev;
    });
  }, [setSearchParams]);

  // Handle closing the category modal (removes URL parameter)
  const handleCloseModal = useCallback(() => {
    setSearchParams(prev => {
      prev.delete('categories');
      return prev;
    });
  }, [setSearchParams]);

  // Handle category selection from modal
  const handleModalCategoryClick = useCallback((category: string) => {
    handleCloseModal();
    handleCategoryClick(category);
    
    // Scroll to the category after modal closes
    // Uses theme scroll offset to account for sticky headers
    setTimeout(() => {
      const element = document.getElementById(`category-${category}`);
      if (element) {
        // Find the scrollable container (Box inside MenuLayout)
        const menuLayoutMain = document.getElementById('menu-layout-main');
        const scrollableContainer = menuLayoutMain?.querySelector('div[style*="overflow-y"]') as HTMLElement | null;
        
        const headerOffset = theme.other?.layout?.scrollOffset ?? 100;
        
        if (scrollableContainer) {
          // Calculate scroll position relative to the scrollable container
          const containerRect = scrollableContainer.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const elementRelativeTop = elementRect.top - containerRect.top;
          const targetScrollTop = scrollableContainer.scrollTop + elementRelativeTop - headerOffset;
          
          scrollableContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        } else {
          // Fallback: use scrollIntoView
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 300);
  }, [handleCategoryClick, handleCloseModal, theme]);

  // Scroll to active category when it changes (horizontal scroll in nav bar)
  useEffect(() => {
    if (activeCategory) {
      scrollToCategory(activeCategory, categoryRefs);
    }
  }, [activeCategory, scrollToCategory]);

  // Log category updates
  useEffect(() => {
    if (categories.length > 0) {
      console.log('CategoryNav - Categories updated:', categories);
    }
  }, [categories]);

  return (
    <>
      <Box
        mb="md"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: theme.other.layout.zIndex.bottomNav,
          backgroundColor: isDarkMode ? theme.colors.dark[7] : theme.white,
          padding: '6px 0 0',
          borderBottom: `1px solid ${isDarkMode ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          boxShadow: isDarkMode
            ? '0 2px 8px rgba(0,0,0,0.4)'
            : '0 2px 6px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Left control: Food/Drinks toggle when drink section enabled, else hamburger */}
          {drinkCategories && onSectionChange && activeSection !== undefined ? (
            <div
              style={{
                display: 'inline-flex',
                flexShrink: 0,
                marginLeft: '10px',
                marginRight: '6px',
                backgroundColor: 'transparent',
                border: `1px solid ${isDarkMode ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                borderRadius: '999px',
                padding: '2px',
                gap: '2px',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                // Handle clicks that land on the wrapper itself (gaps, border, padding)
                // but not bubbled up from child icon divs
                if (e.target === e.currentTarget) {
                  onSectionChange(activeSection === 'food' ? 'drinks' : 'food');
                }
              }}
            >
              {(['food', 'drinks'] as const).map((section) => {
                const isActive = activeSection === section;
                const Icon = section === 'food' ? IconToolsKitchen2 : IconGlassFull;
                const label = section === 'food' ? t('sectionFood', currentLanguage) : t('sectionDrinks', currentLanguage);
                return (
                  <div
                    key={section}
                    style={{
                      cursor: 'pointer',
                      padding: '5px 8px',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isActive
                        ? (isDarkMode ? theme.colors.orange[4] : theme.colors.orange[6])
                        : (isDarkMode ? theme.colors.dark[3] : theme.colors.gray[4]),
                      transition: 'color 0.15s ease',
                      userSelect: 'none',
                    }}
                    onClick={() => onSectionChange(isActive ? (section === 'food' ? 'drinks' : 'food') : section)}
                    role="tab"
                    aria-selected={isActive}
                    aria-label={label}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSectionChange(isActive ? (section === 'food' ? 'drinks' : 'food') : section);
                      }
                    }}
                  >
                    <Icon size={17} stroke={isActive ? 2.2 : 1.5} />
                  </div>
                );
              })}
            </div>
          ) : (
            <ActionIcon
              variant="light"
              color="orange"
              size="md"
              radius="xl"
              style={{
                marginLeft: '16px',
                marginRight: '8px',
                flexShrink: 0,
              }}
              onClick={handleOpenModal}
              aria-label={t('openCategoriesList', currentLanguage)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenModal();
                }
              }}
            >
              <IconList size={16} />
            </ActionIcon>
          )}

          {/* Scrollable Categories */}
          <ScrollArea
            ref={scrollAreaRef}
            offsetScrollbars
            scrollbarSize={6}
            scrollHideDelay={500}
            type="auto"
            style={{ flex: 1 }}
            aria-label={t('scrollableCategories', currentLanguage)}
          >
            <Group gap="xs" wrap="nowrap" style={{ padding: '0 10px' }}>
              {categories.map((category) => (
                <div
                  key={category}
                  ref={(el) => {
                    if (el) {
                      categoryRefs.current.set(category, el);
                    } else {
                      categoryRefs.current.delete(category);
                    }
                  }}
                >
                  <CategoryItem
                    category={category}
                    isActive={activeCategory === category}
                    onClick={() => handleCategoryClick(category)}
                    theme={theme}
                    currentLanguage={currentLanguage}
                  />
                </div>
              ))}
            </Group>
          </ScrollArea>
        </div>
      </Box>

      {/* Categories Modal */}
      <Modal
        opened={modalOpened}
        onClose={handleCloseModal}
        title={null}
        fullScreen
        radius={0}
        aria-label={t('restaurantInfoAndCategories', currentLanguage)}
        closeButtonProps={{ 'aria-label': t('close', currentLanguage) }}
        // Ensure category modal floats above chat/cart layers
        zIndex={(theme.other?.layout?.zIndex?.notifications ?? 10) + 5}
        overlayProps={{ opacity: 0.55, blur: 2 }}
          styles={{
            body: {
              height: '100%',
              padding: '24px',
              overflowY: 'auto',
              backgroundColor: isDarkMode ? theme.colors.dark[7] : theme.colors.gray[0]
            }
          }}
      >
        <Stack gap="md" style={{ minHeight: '100%' }}>
          {/* Category modal top image (falls back to menu hero image) */}
          {(menu?.categoryTopImageUrl || menu?.imageUrl) && (
            <Box
              style={{
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                aspectRatio: categoryTopImageAspectRatio,
                maxHeight: categoryTopImageMaxHeight,
                borderRadius: theme.defaultRadius,
                backgroundColor: theme.colors.gray[2],
              }}
            >
              {/* Full-width menu image at top of category view */}
              <img
                src={menu?.categoryTopImageUrl || menu?.imageUrl}
                alt={t('menuHeaderAlt', currentLanguage)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
          )}

          {/* Single-line heading: "<restaurant name> menu" */}
          {menu?.restaurant?.name && (
            <Text size="xl" fw={700}>
              {`${menu.restaurant.name} ${t('menuSectionTitle', currentLanguage)}`}
            </Text>
          )}

          {/* Divider below combined restaurant/menu heading */}
          <Divider />

          {/* Categories Section */}
          {drinkCategories && allCategories ? (
            // Grouped view: Food and Drinks sections
            (() => {
              const drinkSet = new Set(drinkCategories);
              const foodCats = allCategories.filter(c => !drinkSet.has(c));
              const drinkCats = allCategories.filter(c => drinkSet.has(c));
              const renderCategoryCard = (category: string) => (
                <Card
                  key={category}
                  shadow="sm"
                  padding="md"
                  radius="sm"
                  withBorder
                  style={{ cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                  onClick={() => handleModalCategoryClick(category)}
                  role="button"
                  tabIndex={0}
                  aria-label={t('goToCategoryAria', currentLanguage, { category })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleModalCategoryClick(category);
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = isDarkMode
                      ? '0 4px 12px rgba(255, 255, 255, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)'
                      : '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Text size="md" fw={500} mb={6} lineClamp={1}>{category}</Text>
                  <Badge variant="light" color="orange" size="xs"
                    aria-label={t('itemsInCategoryAria', currentLanguage, { category, count: categoryCounts[category] || 0 })}
                  >
                    {t('itemsCount', currentLanguage, { count: categoryCounts[category] || 0 })}
                  </Badge>
                </Card>
              );
              return (
                <Stack gap="lg">
                  {foodCats.length > 0 && (
                    <div>
                      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">
                        {t('sectionFood', currentLanguage)}
                      </Text>
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                        {foodCats.map(renderCategoryCard)}
                      </SimpleGrid>
                    </div>
                  )}
                  {drinkCats.length > 0 && (
                    <div>
                      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm">
                        {t('sectionDrinks', currentLanguage)}
                      </Text>
                      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                        {drinkCats.map(renderCategoryCard)}
                      </SimpleGrid>
                    </div>
                  )}
                </Stack>
              );
            })()
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {categories.map((category) => (
                <Card
                  key={category}
                  shadow="sm"
                  padding="md"
                  radius="sm"
                  withBorder
                  style={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  }}
                  onClick={() => handleModalCategoryClick(category)}
                  role="button"
                  tabIndex={0}
                  aria-label={t('goToCategoryAria', currentLanguage, { category })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleModalCategoryClick(category);
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = isDarkMode
                      ? '0 4px 12px rgba(255, 255, 255, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)'
                      : '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Text size="md" fw={500} mb={6} lineClamp={1}>
                    {category}
                  </Text>
                  <Badge
                    variant="light"
                    color="orange"
                    size="xs"
                    aria-label={t('itemsInCategoryAria', currentLanguage, { category, count: categoryCounts[category] || 0 })}
                  >
                    {t('itemsCount', currentLanguage, { count: categoryCounts[category] || 0 })}
                  </Badge>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Stack>
      </Modal>

    </>
  );
}); 